import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { type SnipeItModel } from "@/lib/snipeit/types";

export type { SnipeItModel };
export type Theme = "light" | "dark";

export const MAX_DASHBOARD_MODELS = 6;

export interface BlacklistedConsumable {
  id: number;
  name: string;
}

export interface BlacklistedDevice {
  id: number;
  name: string;
}

interface Settings {
  theme: Theme;
  locationId: number | null;
  apiToken: string;
  dashboardModels: SnipeItModel[];
  consumableBlacklist: BlacklistedConsumable[];
  deviceBlacklist: BlacklistedDevice[];
}

interface SettingsContextValue extends Settings {
  setTheme: (theme: Theme) => void;
  setLocationId: (locationId: number | null) => void;
  setApiToken: (token: string) => void;
  setDashboardModels: (models: SnipeItModel[]) => void;
  setConsumableBlacklist: (items: BlacklistedConsumable[]) => void;
  setDeviceBlacklist: (items: BlacklistedDevice[]) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem("inventory-settings");
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Settings>;
      return {
        theme: parsed.theme ?? "light",
        locationId: parsed.locationId ?? null,
        apiToken: parsed.apiToken ?? "",
        dashboardModels: parsed.dashboardModels ?? [],
        consumableBlacklist: parsed.consumableBlacklist ?? [],
        deviceBlacklist: parsed.deviceBlacklist ?? [],
      };
    }
  } catch {
    // ignore
  }
  return {
    theme: "light",
    locationId: null,
    apiToken: "",
    dashboardModels: [],
    consumableBlacklist: [],
    deviceBlacklist: [],
  };
}

function saveSettings(settings: Settings) {
  localStorage.setItem("inventory-settings", JSON.stringify(settings));
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [settings.theme]);

  function setTheme(theme: Theme) {
    setSettings((prev) => {
      const next = { ...prev, theme };
      saveSettings(next);
      return next;
    });
  }

  function setLocationId(locationId: number | null) {
    setSettings((prev) => {
      const next = { ...prev, locationId };
      saveSettings(next);
      return next;
    });
  }

  function setApiToken(apiToken: string) {
    setSettings((prev) => {
      const next = { ...prev, apiToken };
      saveSettings(next);
      return next;
    });
  }

  function setDashboardModels(models: SnipeItModel[]) {
    setSettings((prev) => {
      const next = { ...prev, dashboardModels: models.slice(0, MAX_DASHBOARD_MODELS) };
      saveSettings(next);
      return next;
    });
  }

  function setConsumableBlacklist(items: BlacklistedConsumable[]) {
    setSettings((prev) => {
      const next = { ...prev, consumableBlacklist: items };
      saveSettings(next);
      return next;
    });
  }

  function setDeviceBlacklist(items: BlacklistedDevice[]) {
    setSettings((prev) => {
      const next = { ...prev, deviceBlacklist: items };
      saveSettings(next);
      return next;
    });
  }

  return (
    <SettingsContext.Provider
      value={{
        ...settings,
        setTheme,
        setLocationId,
        setApiToken,
        setDashboardModels,
        setConsumableBlacklist,
        setDeviceBlacklist,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside <SettingsProvider>");
  return ctx;
}
